import { useState, useEffect, useRef } from "react";
import Map, { Source, Layer, Marker, Popup } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import type { MapMouseEvent, MapRef } from "react-map-gl/maplibre";
import { FeatureCollection, Position } from "geojson";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse, faTrain, faXmark } from "@fortawesome/free-solid-svg-icons";
import maplibregl, { DataDrivenPropertyValueSpecification } from "maplibre-gl";
import { fitBounds } from "viewport-mercator-project";
import * as turf from "@turf/turf";

type radiusType = 5000 | 10000 | 15000 | 20000;

const layersName = "Deutschland_villages_no_trains";

const sourceLayers = [
  // {
  //   name: `${layersName}_1000`,
  //   id: 1000,
  //   count: 38483,
  // },
  {
    name: `${layersName}_5000`,
    id: 5000,
    count: 18207,
  },
  {
    name: `${layersName}_10000`,
    id: 10000,
    count: 4255,
  },
  {
    name: `${layersName}_15000`,
    id: 15000,
    count: 695,
  },
  {
    name: `${layersName}_20000`,
    id: 20000,
    count: 94,
  },
];

const defaultHeatmapStyle = [
  "interpolate",
  ["linear"],
  ["get", "point_count"],
  0,
  0,
] as DataDrivenPropertyValueSpecification<number> | undefined;

const openRouteUrl =
  "https://api.openrouteservice.org/v2/directions/driving-car?api_key=5b3ce3597851110001cf62486eab3cdce0ff41f9b6cc5107b0e378e1";

function AppIsolated() {
  const mapRef = useRef<MapRef | null>(null);
  const [geojsonData, setGeojsonData] = useState(null);
  const [showVillages, setShowVillages] = useState(true);
  const [selectedFeature, setSelectedFeature] =
    useState<maplibregl.MapGeoJSONFeature>();
  const [hoveredFeature, setHoveredFeature] =
    useState<maplibregl.MapGeoJSONFeature>();

  const [markers, setMarkers] = useState<
    { longitude: number; latitude: number }[]
  >([]);
  const [layerType, setLayerType] = useState<"heatmap" | "circle">("heatmap");

  const [heatmapWeight, setHeatmapWeight] = useState<number>(104);

  const [routeCollections, setRouteCollections] = useState<FeatureCollection[]>(
    []
  );
  const selectedFeatureRef = useRef(selectedFeature);

  const [showPopup, setShowPopup] = useState<boolean>(true);

  const [radius, setRadius] = useState<radiusType>(5000);
  const radiusRef = useRef(radius);

  const [heatmapWeightStyle, setHeatmapWeightStyle] = useState<
    DataDrivenPropertyValueSpecification<number> | undefined
  >(defaultHeatmapStyle);

  const resolveRoutesFetch = async (fetchPromises: Promise<Response>[]) => {
    const responses: Response[] = await Promise.all(fetchPromises);

    if (responses[0].ok) {
      const data: FeatureCollection[] = await Promise.all(
        responses.map(
          (response) => response.json() as Promise<FeatureCollection>
        )
      );

      setRouteCollections(data);
    } else {
      // DO something when the route is not found
    }
  };

  const buildRouteURL = (start: Position, end: Position) => {
    return fetch(
      `${openRouteUrl}&start=${start.join(",")}&end=${end.join(",")}`
    );
  };

  useEffect(() => {
    // Fetch the GeoJSON data from ArcGIS FeatureServer
    const fetchGeojson = async () => {
      const response = await fetch(
        "https://services2.arcgis.com/jUpNdisbWqRpMo35/arcgis/rest/services/Bundesl%C3%A4ndergrenzen_2023/FeatureServer/0/query?where=1%3D1&outFields=*&outSR=4326&f=geojson"
      );
      const data = await response.json();
      setGeojsonData(data);
    };
    fetchGeojson();
  }, []);

  useEffect(() => {
    if (routeCollections.length === 0) {
      return;
    }
    const allFeatures: GeoJSON.Feature[] = [];
    for (const feature of routeCollections) {
      allFeatures.push(...feature.features);
    }

    const combinedFeatures: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [...allFeatures],
    };

    const mapWidth = window.innerWidth;
    const mapHeight = window.innerHeight;
    const bbox = turf.bbox(combinedFeatures);

    const { longitude, latitude, zoom } = fitBounds({
      width: mapWidth,
      height: mapHeight,
      bounds: [
        [bbox[0], bbox[1]], // Southwest corner
        [bbox[2], bbox[3]], // Northeast corner
      ],
      padding: 150, // Optional: add padding to the bounds
    });

    // Access the map instance via ref
    const map = mapRef.current?.getMap();
    if (map) {
      // Use map.flyTo to animate the transition
      map.flyTo({
        duration: 1000,
        center: [longitude, latitude], // The calculated center of the points
        zoom: zoom, // Adjust zoom level to fit the points (you can calculate this dynamically too)
        speed: 1.2, // Controls the animation speed
        curve: 1.42, // The curve of the animation (default is 1.42)
        essential: true, // Ensures that the animation is essential (avoids user interruption)
      });
    }
  }, [routeCollections]);

  useEffect(() => {
    selectedFeatureRef.current = selectedFeature;

    const stringCoordinates =
      selectedFeature?.properties?.station_coordinates ?? false;

    if (!stringCoordinates) {
      return;
    }
    setMarkers([]);
    setRouteCollections([]);
    const closest_station = JSON.parse(stringCoordinates);
    const { coordinates } = selectedFeature?.geometry as GeoJSON.Point;
    const routesRequest = [];

    setMarkers((prevMarkers) => [
      ...prevMarkers,
      { longitude: closest_station[0], latitude: closest_station[1] },
    ]);

    routesRequest.push(
      buildRouteURL(coordinates, [
        closest_station[0],
        closest_station[1],
      ] as GeoJSON.Position)
    );

    resolveRoutesFetch(routesRequest);
  }, [selectedFeature]);

  const handleMouseEnter = (
    map: MapRef | null,
    features: maplibregl.MapGeoJSONFeature[]
  ) => {
    if (map) {
      map.getCanvas().style.cursor = "pointer";
      setHoveredFeature(features[0]);
      setShowPopup(true);
    }
  };

  const handleMouseLeave = (map: MapRef | null) => {
    if (map) {
      setShowPopup(false);
      map.getCanvas().style.cursor = "default";
    }
  };

  const handleOnClick = (map: MapRef | null, event: MapMouseEvent) => {
    if (map) {
      const currentZoom = map.getZoom();
      const { point } = event;

      if (selectedFeatureRef.current) {
        return;
      }

      const features = map?.queryRenderedFeatures(point, {
        layers: [`layer_${layersName}_${radiusRef.current}`],
      });

      if (features.length > 0) {
        const feature = features[0];

        if (!feature.properties.clustered) {
          setSelectedFeature(feature);
          setShowVillages(false);
        } else {
          const { coordinates } = feature.geometry as GeoJSON.Point;
          const featureZoom: number = !feature.properties.clustered
            ? 14
            : currentZoom > 10.5
            ? currentZoom
            : currentZoom < 8
            ? 8
            : 10.5;

          // Fly to the first feature's coordinates and zoom in smoothly
          map.flyTo({
            duration: 2000,
            center: coordinates as maplibregl.LngLatLike, // Use feature's coordinates
            zoom: featureZoom, // Target zoom level
            curve: 1.5, // Control the "smoothness" of the flyTo (larger values make it smoother)
            essential: true, // Ensure the transition is not interrupted by user gestures
          });
        }
      }
    }
  };

  useEffect(() => {
    setHeatmapWeightStyle([
      "interpolate",
      ["linear"],
      ["get", "point_count"],
      0,
      0,
      heatmapWeight * 0.5,
      1,
      heatmapWeight,
      2,
    ] as DataDrivenPropertyValueSpecification<number> | undefined);
  }, [heatmapWeight]);

  useEffect(() => {
    radiusRef.current = radius;
  }, [radius]);

  const calculateWeight = (pRadius: radiusType) => {
    let counter = 0;
    // let totalFeatures = 0;

    mapRef.current
      ?.querySourceFeatures(
        `source_Deutschland_villages_no_trains_${pRadius}`,
        {
          sourceLayer: `Deutschland_villages_no_trains_${pRadius}`,
        }
      )
      .forEach((feature) => {
        counter =
          feature.properties.point_count > counter
            ? feature.properties.point_count
            : counter;

        // if (feature.properties.clustered) {
        //   totalFeatures += feature.properties.point_count;
        // } else {
        //   totalFeatures += 1;
        // }
      });

    if (counter !== 0) {
      setHeatmapWeight(counter);
    }
  };

  const onLoadMap = () => {
    calculateWeight(radius);

    mapRef.current?.on("click", (event) => {
      handleOnClick(mapRef.current, event);
    });

    for (const layer of sourceLayers) {
      mapRef.current?.on(
        "mouseenter",
        `layer_${layer.name}`,
        (
          event: MapMouseEvent & {
            features?: maplibregl.MapGeoJSONFeature[];
          } & Object
        ) => {
          const features = event.features;

          if (features && !features[0].properties.clustered) {
            handleMouseEnter(mapRef.current, features);
          }
        }
      );
      mapRef.current?.on("mouseleave", `layer_${layer.name}`, () => {
        handleMouseLeave(mapRef.current);
      });
    }

    mapRef.current?.on(
      "data",
      (
        data: maplibregl.MapDataEvent & {
          sourceId: string;
          isSourceLoaded: boolean;
        }
      ) => {
        const sourceId = `source_${layersName}_${radiusRef.current}`;

        if (sourceId === data.sourceId && data.isSourceLoaded) {
          calculateWeight(radiusRef.current);
        }
      }
    );
  };

  const sourceVillage = sourceLayers.find(
    (source) => source.name === `${layersName}_${radius}`
  );

  return (
    <div className="w-full h-[calc(100vh-80px)] md:h-[calc(100vh-108px)]">
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: 10.4515,
          latitude: 51.1657,
          zoom: window.innerWidth <= 768 ? 4.5 : 5.1,
        }}
        style={{ width: "100%", height: "100%" }}
        onZoom={() => {
          if ((mapRef.current?.getZoom() as number) >= 9) {
            setLayerType("circle");
          } else {
            setLayerType("heatmap");
          }
        }}
        mapStyle="https://api.maptiler.com/maps/dataviz/style.json?key=zp4qjKHPh9o4pDUkcABM"
        onLoad={onLoadMap}
        minZoom={4.5}
        maxZoom={14}
        maxBounds={[
          [-31, 34],
          [40, 72],
        ]}
      >
        <Source id="Bundeslaender" type="geojson" data={geojsonData}>
          <Layer
            id="Bundeslaender-layer"
            type="line"
            paint={{
              "line-color": "#000",
              "line-opacity": 0.5,
              "line-width": 1,
            }}
          />
        </Source>

        {showVillages &&
          sourceLayers.map((source) => {
            if (source.id !== radius) {
              return null;
            } else {
              return (
                <Source
                  key={source.name}
                  id={`source_${source.name}`}
                  type="vector"
                  tiles={[
                    `https://diegovargasg.github.io/remotevillages/tiles/${source.name}/{z}/{x}/{y}.pbf`,
                  ]}
                >
                  {layerType === "heatmap" ? (
                    <Layer
                      id={`layer_${source.name}`}
                      type="heatmap"
                      source={`source_${source.name}`}
                      source-layer={source.name}
                      paint={{
                        "heatmap-weight": heatmapWeightStyle,
                        "heatmap-intensity": 1,
                        "heatmap-color": [
                          "interpolate",
                          ["linear"],
                          ["heatmap-density"],
                          0,
                          "rgba(33,102,172,0)",
                          0.2,
                          "rgb(103,169,207)",
                          0.4,
                          "rgb(209,229,240)",
                          0.6,
                          "rgb(253,219,199)",
                          0.8,
                          "rgb(239,138,98)",
                          0.9,
                          "rgb(255,201,101)",
                        ],
                        "heatmap-radius": 20,
                        "heatmap-opacity": 0.9,
                      }}
                    />
                  ) : (
                    <>
                      <Layer
                        id={`layer_${source.name}`}
                        source={`source_${source.name}`}
                        source-layer={source.name}
                        type="circle"
                        filter={["!", ["has", "point_count"]]} // Show only unclustered points
                        paint={{
                          "circle-radius": {
                            default: 5,
                            stops: [
                              [9, 2],
                              [10, 7],
                              [11, 9],
                              [12, 11],
                            ],
                          } as
                            | DataDrivenPropertyValueSpecification<number>
                            | undefined,
                          "circle-stroke-color": "#f3e8ff",
                          "circle-stroke-width": 2,
                          "circle-color": "#702f5b",
                          "circle-opacity": 1,
                          "circle-translate": [0, 25],
                        }}
                      />
                    </>
                  )}
                </Source>
              );
            }
          })}
        {routeCollections &&
          routeCollections.map((routeCollection, index) => (
            <Source
              key={index}
              id={`source_${index}`}
              type="geojson"
              data={routeCollection}
            >
              <Layer
                id={`layer_${index}`}
                type="line"
                paint={{ "line-color": "#0ea5e9", "line-width": 3 }}
              />
            </Source>
          ))}
        {selectedFeature &&
          markers.map((marker, index) => {
            return (
              <Marker
                key={index}
                longitude={marker.longitude}
                latitude={marker.latitude}
              >
                <div
                  style={{ background: "#fff", width: "21px", height: "21px" }}
                >
                  <FontAwesomeIcon icon={faTrain} size="2x" color="#2c65ad" />
                </div>
              </Marker>
            );
          })}
        {selectedFeature && (
          <Marker
            longitude={
              (selectedFeature.geometry as GeoJSON.Point).coordinates[0]
            }
            latitude={
              (selectedFeature.geometry as GeoJSON.Point).coordinates[1]
            }
          >
            <div
              style={{
                width: "25px", // Circle diameter
                height: "25px", // Circle diameter
                backgroundColor: "#fff", // Background color of the circle
                border: "solid 2px #702f5b",
                borderRadius: "50%", // Makes the div circular
                display: "flex", // Flexbox to center content
                alignItems: "center", // Vertically centers the icon
                justifyContent: "center", // Horizontally centers the icon
                color: "#702f5b", // FontAwesome icon color
                transform: "translate(-50%, -50%)", // Centers the marker relative to the coordinates
              }}
            >
              <FontAwesomeIcon icon={faHouse} size="lg" />
            </div>
          </Marker>
        )}
        {showPopup && hoveredFeature && (
          <Popup
            longitude={
              (hoveredFeature.geometry as GeoJSON.Point).coordinates[0]
            }
            latitude={(hoveredFeature.geometry as GeoJSON.Point).coordinates[1]}
            anchor="bottom"
            closeButton={false}
            onClose={() => setShowPopup(false)}
            className="font-semibold text-gray-500"
          >
            <>
              <FontAwesomeIcon
                icon={faHouse}
                size="lg"
                className="mr-1"
                color="#702f5b"
              />
              {hoveredFeature.properties.village_name}
            </>
          </Popup>
        )}
      </Map>
      {selectedFeature && (
        <div className="absolute top-12 md:top-16 left-2 z-50 bg-white p-2 shadow-lg rounded-md w-80 text-xs md:text-base text-gray-500">
          <span
            onClick={() => {
              setSelectedFeature(undefined);
              setShowVillages(true);
              setMarkers([]);
              setRouteCollections([]);
            }}
            className="cursor-pointer"
          >
            <FontAwesomeIcon
              className="absolute top-2 right-2"
              icon={faXmark}
            />
          </span>
          <h2 className="text-center font-bold text-xs mb-4">
            Journey to nearest train station:
          </h2>
          <div className="my-2">
            <FontAwesomeIcon
              icon={faHouse}
              size="sm"
              color="#702f5b"
              className="mr-2"
            />
            <span className="font-bold text-xs md:text-xs mr-1">
              Selected village:{" "}
            </span>
            <span className="text-xs md:text-xs mr-1">
              {selectedFeature.properties.village_name}
            </span>
          </div>
          <div className="my-2">
            <FontAwesomeIcon
              icon={faTrain}
              size="sm"
              color="#2c65ad"
              className="mr-2"
            />
            <span className="font-bold text-xs md:text-xs mr-1">
              Nearest station:{" "}
            </span>
            <span className="text-xs md:text-xs mr-1">
              {selectedFeature.properties.station_name}
            </span>
          </div>
          {routeCollections.map((route) => {
            if (route.features.length > 0) {
              const summary = route.features[0].properties?.summary;
              return (
                <>
                  <div className="my-2">
                    <span className="font-bold text-xs md:text-xs mr-1 pl-5">
                      Journey distance:
                    </span>{" "}
                    <span className="text-xs md:text-xs">
                      {Math.ceil(summary.distance / 1000)} km
                    </span>
                  </div>
                  <div className="my-2">
                    <span className="font-bold text-xs md:text-xs mr-1 pl-5">
                      Journey duration:
                    </span>{" "}
                    <span className="text-xs md:text-xs">
                      {Math.ceil(summary.duration / 60)} min
                    </span>
                  </div>
                </>
              );
            }
            return null;
          })}
        </div>
      )}
      {!selectedFeature && (
        <div className="absolute top-12 md:top-16 left-2 z-50 bg-white py-4 px-2 shadow-lg rounded-md w-60 md:w-72 text-xs md:text-sm">
          <div className="relative mb-6">
            <p className="text-gray-500 text-xs mb-2">
              Villages where the nearest train station is in a radius of at
              least:
            </p>
            <label htmlFor="labels-range-input" className="sr-only">
              Labels range
            </label>
            <input
              id="labels-range-input"
              type="range"
              value={radius}
              min={5000}
              max={20000}
              step={5000}
              onChange={(e) => {
                const number =
                  parseInt(e.currentTarget.value) === 0
                    ? 5000
                    : (parseInt(e.currentTarget.value) as radiusType);
                setRadius(number);
              }}
              className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-xs text-gray-500 absolute start-0 -bottom-6">
              5km
            </span>
            <span className="text-xs text-gray-500 absolute start-1/3 -translate-x-3 rtl:translate-x-1 -bottom-6">
              10km
            </span>
            <span className="text-xs text-gray-500 absolute start-2/3 -translate-x-4 rtl:translate-x-1/3 -bottom-6">
              15km
            </span>
            <span className="text-xs text-gray-500 absolute end-0 -bottom-6">
              20km
            </span>
          </div>
        </div>
      )}

      <div className="absolute bottom-12 md:bottom-16 left-2 z-50 bg-white p-2 shadow-lg rounded-md">
        <div>
          <div
            className="w-4 h-4 rounded-full inline-block border-2 mr-2 align-middle"
            style={{ borderColor: "#f3e8ff", backgroundColor: "#702f5b" }}
          ></div>
          <span className="mr-3 text-xs text-gray-500">
            {sourceVillage?.count} villages found
          </span>
        </div>
      </div>
    </div>
  );
}

export default AppIsolated;
